import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import type { Sport } from '#src/__generated__/globalTypes.ts';
import { stripPrefix } from '#src/lib/i18n.ts';
import type { PathParams } from '#src/lib/routing/generateSportPath.ts';
import {
  SPORT_PATH_MAP,
  generateSportPath,
  matchAnySportPath,
} from '#src/lib/routing/generateSportPath.ts';
import {
  preferredSport,
  sportFromPathPrefix,
} from '#src/lib/routing/sportFromPath.ts';

import { SportContext, sportAnalyticTag } from './index.ts';

type Props = {
  children: ReactNode;
  sport?: Sport;
};

type ChosenSport = {
  sport: Sport;
  pathSportAtChoice: Sport | undefined;
};

export const SportProvider = ({ children, sport: forcedSport }: Props) => {
  const { pathname } = useLocation();
  const [chosenSport, setChosenSport] = useState<ChosenSport>();
  const [sportPreference] = useState(preferredSport);

  const pathSport = sportFromPathPrefix(stripPrefix(pathname));

  const setSport = useCallback(
    (sport: Sport) => setChosenSport({ sport, pathSportAtChoice: pathSport }),
    [pathSport]
  );

  const chosenSportStillApplies =
    !!chosenSport &&
    (pathSport === undefined || pathSport === chosenSport.pathSportAtChoice);
  const sport =
    forcedSport ||
    (chosenSportStillApplies ? chosenSport.sport : pathSport) ||
    sportPreference;

  const value = useMemo(
    () => ({
      sport,
      sportAnalyticsTag: sportAnalyticTag[sport],
      setSport,
      // wrap the `generateSportPath` to handle the default sport
      generateSportContextPath: <Path extends string>(
        path: Path,
        {
          params,
          sport: s = sport,
        }: {
          params?: PathParams<Path>;
          sport?: Sport;
        } = {}
      ) => generateSportPath(path, { params, sport: s }),
      // eslint-disable-next-line @typescript-eslint/no-shadow
      matchAnySportPath: (pattern: string, pathname: string, s?: Sport) =>
        matchAnySportPath(pattern, pathname, s || sport),
      generateSportContextPathPattern: (pattern: string) => {
        return `${SPORT_PATH_MAP[sport]}${pattern}`;
      },
    }),
    [sport, setSport]
  );

  return <SportContext value={value}>{children}</SportContext>;
};
